/**
 * Shared timestamp formatting.
 *
 * One implementation for every "5m ago" in the app — the comment cards, the
 * review-run picker, and anything that follows. Formatting is built by hand
 * rather than via `Intl` because the ICU output for `month: 'short'` varies by
 * locale and Node build ("Jul" vs "July" vs "Jul."), and the UI wants one
 * stable shape everywhere.
 */

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS
const WEEK_MS = 7 * DAY_MS

/** `12 Jul`, or `12 Jul 2025` when the year is worth stating. */
function formatDate(date: Date, withYear: boolean): string {
  const day = date.getDate()
  const month = MONTHS[date.getMonth()]
  return withYear ? `${day} ${month} ${date.getFullYear()}` : `${day} ${month}`
}

/**
 * Compact age for a timestamp:
 * `just now` (<60s) | `5m ago` (<60m) | `3h ago` (<24h) | `6d ago` (<7d) |
 * `12 Jul` (≥7d, same year) | `12 Jul 2025` (≥7d, prior year).
 *
 * Future timestamps read as "just now" — a clock skew between the server and
 * the browser should never surface as a negative age.
 *
 * @param iso ISO-8601 timestamp.
 * @param now Reference point; pass explicitly in tests to keep them deterministic.
 */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso)
  const thenMs = then.getTime()
  if (Number.isNaN(thenMs)) return ''

  const elapsed = now.getTime() - thenMs
  if (elapsed < MINUTE_MS) return 'just now'
  if (elapsed < HOUR_MS) return `${Math.floor(elapsed / MINUTE_MS)}m ago`
  if (elapsed < DAY_MS) return `${Math.floor(elapsed / HOUR_MS)}h ago`
  if (elapsed < WEEK_MS) return `${Math.floor(elapsed / DAY_MS)}d ago`

  return formatDate(then, then.getFullYear() !== now.getFullYear())
}

/**
 * Day-month-year only, e.g. `12 Jul 2026` — the AU-order date shape the rest of
 * this module already builds, exposed for surfaces that want the date without a
 * time of day. Built by hand for the same reason `formatDate` is: ICU's short
 * month varies by Node build.
 */
export function absoluteDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return formatDate(date, true)
}

/**
 * Day and short month only, e.g. `19 Feb` — the compact AU-order date used for
 * deadlines on Home and My Work. Empty string for a missing or unparseable date.
 */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return formatDate(date, false)
}

/**
 * Full local date-time for `title` tooltips, e.g. `12 Jul 2026, 3:41 pm` —
 * the exact value behind a relative label.
 */
export function absoluteTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''

  const hours24 = date.getHours()
  const meridiem = hours24 < 12 ? 'am' : 'pm'
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${formatDate(date, true)}, ${hours12}:${minutes} ${meridiem}`
}
